/* eslint-disable no-underscore-dangle */
/* eslint-disable prefer-const */
import * as React from 'react';
import { navigate } from 'gatsby';
import { StoreContext } from './store-context';
import { trackViewedProduct } from '../utils/tracking';
import Helpers from '../components/ProductOptions/helpers';

export const ProductFormContext = React.createContext({});

export const ProductFormProvider = ({
	location,
	//	Data
	children,						//	Child components to render inside
	product,						//	(Object)		The Product Object used to initialize the product-form context.

	//	User Callback(s)
	onVariantChange,				//	*optional* (Function):	User-defined callback that is invoked when variant changes.
	onVariantChangeDependencies,	//	*optional* (Array):		User-defined callback function array of dependencies for use in React.useCallback() .

	//	User Settings
	showErrors: defaultShowErrors,	//	*optional* (Bool):	Show 'hints' to user on Product Option selectors that need more selecting.
	hideMoreSwatches,

	//	Note: For renderingScheme & sortOrder, the keys defined going into the
	//  ContextProvider must be the slugified optionGroup.name string. Such as
	//  that returned by the Helpers.slugify() method.
	renderingScheme: defaultRenderingScheme = {},	//	*optional* (Object):	Allows control of how individual Product Option Groups should be rendered ('button','select','swatch'). Example: {0:'button',1:'select',2:'swatch'}
	sortOrder: defaultSortOrder = [],	//	*optional* (Array)	Allows control of the order of which the optionGroups are rearranged during render.

	//	User Settings: Variant Preselection
	preselectedVariant: defaultPreselectedVariant,						//	*optional* (Object):	The Variant object to pre-select Product Options for
	useUrlParameter,													//	*optional* (Bool):	Read & write variant.id changes to the browser's address bar.
	preselect1stAvailableVariant: defaultPreselect1stAvailableVariant,	//	*optional* (Bool):	Pre-select the 1st available Variant onload.
	usePrettyUrlParameters,												//	*optional* (Bool):	Instead of URL parameter of `variant=123456789102`, use `color=blue&size=large&weight=15-lb`

	//	User Settings: Variant Navigation
	useReplaceState,				//	*optional* (Bool):	When true, ProductOption selections that change the url will not create a history.
}) => {
	//	-----------------------------------
	//	Prepare incoming properties for use
	//	-----------------------------------
	//	eslint-disable-next-line no-unused-vars
	const exampleOnVariantChange = variant => { /* console.log('Variant Changed', variant); */ };
	//	eslint-disable-next-line react-hooks/exhaustive-deps
	const _onVariantChange = React.useCallback(
		onVariantChange || exampleOnVariantChange,
		onVariantChangeDependencies || []
	);

	//	----------
	//	Dependency | Used to fetch product data
	//	----------
	const {
		client,
	} = React.useContext(StoreContext);

	//	----------------------
	//	Pre-State Computations
	//		Variant Preselection
	//			in the following order from:
	//			-	URL get parameter
	//			-	The fact that there's only one of Product.variants; the 'default' variant
	//			-	The first 'available' variant
	//	----------------------
	const [preselect1stAvailableVariant, setPreselect1stAvailableVariant] = React.useState(defaultPreselect1stAvailableVariant);	//	eslint-disable-line max-len

	// The first state variable(above) is to prevent recurssion on mount.
	// The second state variable(below) is to prevent recurssion when fetching availability map
	// eslint-disable-next-line max-len
	// const [preselect1stAvailableVariantAfterAvailability, setPreselect1stAvailableVariantAfterAvailability] = React.useState(defaultPreselect1stAvailableVariant);
	const [preselect1stAvailableVariantAfterAvailability, setPreselect1stAvailableVariantAfterAvailability] = React.useState(false);

	const preselectedVariant = () => {
		let urlVariant;

		if (usePrettyUrlParameters) {
			urlVariant = Helpers.getVariantFromUrlPrettyParameters(product);
		} else {
			urlVariant = Helpers.getVariantFromUrl(product);
		}

		let _preselectedVariant;
		switch (true) {
		case urlVariant != null:	//	Don't you dare tell me to use eqeqeq here.
			// console.log('Preselecting Variant from URL');		//	Debugging Only
			_preselectedVariant = urlVariant;
			break;
		case defaultPreselectedVariant != null:
			_preselectedVariant = defaultPreselectedVariant;
			break;
		case product.variants?.length === 1:
			//	Select the only variant that exists.
			// console.log('Preselecting default Variant');			//	Debugging Only
			[_preselectedVariant] = product.variants;
			break;
		default:
			//	Find first available variant & select it.
			// eslint-disable-next-line quotes
			// console.log("Preselecting first 'available' Variant");	//	Debugging Only
			if (preselect1stAvailableVariant) {
				_preselectedVariant = product.variants
					.sort((a, b) => {
						if (a.position > b.position) return 1;
						if (a.position < b.position) return -1;
						return 0;
					})
					.find(_variant => { return _variant.availableForSale; });
				setPreselect1stAvailableVariant(false);	//	Consider moving this outside of switch(){} if more logic is added in the future
			}
		}// switch
		return _preselectedVariant;
	};

	//	Even though we set the default preselectedVariant into state, it does not update
	//	the browser's address bar here. That happens a handful of lines down from here.
	const [variant, _setVariant] = React.useState(preselectedVariant);
	//	------------
	//	Proxy Setter to perform URL updates before normal state setting behavior
	//	------------
	const setVariant = React.useCallback((_variant, replaceState) => {
		//	Update the URL to match selected Variant
		if (useUrlParameter) {
			// eslint-disable-next-line
			// if (typeof window !== 'undefined' && Helpers.getVariantFromUrl(product) != _variant) {
			if (typeof window !== 'undefined') {
				const url = new URL(window.location);
				if (!_variant?.shopifyId) {
					url.searchParams.delete('variant');
				} else {
					const variantAdminId	=	_variant.shopifyId.split('/').pop();
					url.searchParams.set('variant', variantAdminId);
				}

				//	Denested from above if(){} for readability
				if (usePrettyUrlParameters) {
					url.searchParams.delete('variant');
					_variant?.selectedOptions.forEach(selectedOption => {
						url.searchParams.set(
							Helpers.slugify(selectedOption.name),
							Helpers.slugify(selectedOption.value)
						);
					});
				}

				const params = url.searchParams.toString();
				navigate([
					url.pathname,
					params ? '?' : '',
					params,
				].join(''), {
					replace: useReplaceState && replaceState,
					state: {
						disableScrollUpdate: true,
						prevPath: location?.pathname?.includes('search') ? 'search results' : location?.pathname || '',
					},
				});
			}
		}
		//	Invoke the User's custom callback
		_onVariantChange(_variant);
		//	Invoke the Parent's setter function.
		_setVariant(_variant);
	}, [useUrlParameter, useReplaceState]); // eslint-disable-line

	//	We invoke the following to update the browser's address bar onload. It should realize
	//	the state object is already the same and exit after only the address change.
	// TENTATIVELY DEPRECATED
	// React.useEffect(() => {
	// 	if (preselectedVariant) {
	// 		setVariant(preselectedVariant);
	// 	}
	// }, [setVariant, preselectedVariant]);

	//	---------------------------------------------------------------
	//	Availability
	//	-	We don't want to statically render product options.
	//		This is because product availability changes gradually after
	//			the time our static build was generated.
	//		This makes the data used for the static rendering 'stale' and no-longer accurate.
	//		Thus, on every page-load, we need to fetch the most current real-time
	//			availability from Shopify before rendering the Product Options.
	//	---------------------------------------------------------------
	const [isLoading, setIsLoading] = React.useState(true);
	const [availabilityMap, setAvailabilityMap] = React.useState(null);	//	Default to null so we can easily detect when it's not initialized.

	React.useEffect(() => {
		client.product.fetch(product.shopifyId || product.id).then(_p => {
			console.log({ _p });
			// console.log('Did fetch latest variant availability');
			const availMap = {};
			_p?.variants.forEach(_v => {
				console.log({ _v });
				availMap[_v.id] = _v.available;
				return null;
			}); //  Load object with properties as ${id}:${available}
			setAvailabilityMap(availMap);
			setIsLoading(false);
			return availMap;
		})
			.then(availMap => {
				// set the variant if dictated by class config OR if current variant is unavailable
				if (preselect1stAvailableVariantAfterAvailability || !availMap[variant?.shopifyId]) {
					const _preselectedVariant = product.variants
						.sort((a, b) => {
							if (a.position > b.position) return 1;
							if (a.position < b.position) return -1;
							return 0;
						})
						.find(_variant => {
							return availMap[_variant.shopifyId];
						});
					if (_preselectedVariant === undefined) {
						setVariant(product.variants[0]);
					} else {
						setVariant(_preselectedVariant);
					}
					setPreselect1stAvailableVariantAfterAvailability(false);
				}
			});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client.product, product.shopifyId, product.id]);

	//	----------------------------
	//	Show Option Selection Errors
	//	----------------------------
	const [_showErrors, setShowErrors] = React.useState(defaultShowErrors);

	//	---------
	//	Helper(s)
	//	---------
	const isVariantAvailable = () => {
		return availabilityMap && !!availabilityMap[variant?.shopifyId];
	};

	React.useEffect(() => {
		trackViewedProduct(product, variant);
	}, []);

	//	------
	//	Output
	//	------
	return (
		<ProductFormContext.Provider
			// eslint-disable-next-line react/jsx-no-constructed-context-values
			value={{
				//	Data
				product,
				variant,
				//	Ajax Data
				availabilityMap,
				//	Helpers
				isVariantAvailable,
				//	State
				showErrors: _showErrors,
				//	State Setters
				setVariant,
				setShowErrors,
				// Status
				isLoading,
				//	User preferences
				renderingScheme: defaultRenderingScheme,
				sortOrder: defaultSortOrder,
				// User Settings
				hideMoreSwatches,
			}}
		>
			{children}
		</ProductFormContext.Provider>
	);
};
